<!--
  WorkAttachments.vue — reusable attachment panel for a saved Work item.

  Renders the 50MB/all-types WorkFileUploader plus the list of already-uploaded
  files (via MediaViewer), persisting the file list to work.metadata.uploaded_images
  on the work record. Requires a SAVED work (work-id) — attachments hang off the
  Work's id/metadata.

  Used by WorkLogger (after a work item is created, in ALL work-type branches) and
  by GenericWorkForm, so document upload is available for every work type — not
  just the JSON-schema/GenericWorkForm fallback, and not limited to images/5MB.
-->
<template>
  <div class="border border-gray-200 rounded p-3">
    <h4 class="text-xs font-semibold text-gray-900 mb-2">Attachments</h4>

    <div v-if="workId" class="mb-3">
      <WorkFileUploader
        :related-object-id="workId"
        related-object-content-type-model="work"
        :disabled="busy"
        @file-uploaded="addFile"
      />
    </div>
    <div v-else class="mb-3 text-xs text-gray-500 italic">
      Save the work item first to upload attachments
    </div>

    <div v-if="files.length > 0" class="space-y-2">
      <div class="text-xs text-gray-500 font-medium">Uploaded files:</div>
      <div v-for="f in files" :key="f.key" class="flex items-center gap-2">
        <MediaViewer
          :can-delete="!busy"
          :media-key="f.key"
          :filename="f.filename"
          @media-deleted="removeFile"
        />
      </div>
    </div>
    <div v-else class="text-xs text-gray-400">No attachments yet</div>
  </div>
</template>

<script>
import axios from 'axios';
import WorkFileUploader from '@/components/widgetcomponents/WorkFileUploader.vue';
import MediaViewer from '@/components/widgetcomponents/MediaViewer.vue';

export default {
  name: 'WorkAttachments',
  components: { WorkFileUploader, MediaViewer },
  props: {
    // The saved Work id. Attachments require a persisted work record.
    workId: { type: [Number, String], default: null },
    // Optional seed metadata (avoids a fetch if the caller already has it).
    initialMetadata: { type: Object, default: null },
    currentUser: { type: Object, default: null },
  },
  emits: ['files-changed'],
  data() {
    return {
      files: [],       // [{ key, filename, uploaded_by }]
      metadata: {},    // the work's full metadata (merged on PATCH)
      busy: false,
    };
  },
  watch: {
    workId: {
      immediate: true,
      handler(id) {
        if (id) this.loadExisting();
      },
    },
  },
  methods: {
    async loadExisting() {
      if (this.initialMetadata) {
        this.metadata = { ...this.initialMetadata };
        this.files = Array.isArray(this.metadata.uploaded_images)
          ? [...this.metadata.uploaded_images] : [];
        return;
      }
      try {
        const { data } = await axios.get(`/api/work/${this.workId}/`);
        this.metadata = data.metadata || {};
        this.files = Array.isArray(this.metadata.uploaded_images)
          ? [...this.metadata.uploaded_images] : [];
      } catch {
        // Non-fatal — show empty; upload still works and will PATCH.
        this.metadata = this.metadata || {};
        this.files = [];
      }
    },

    async persist(nextFiles) {
      this.busy = true;
      try {
        await axios.patch(`/api/work/${this.workId}/`, {
          metadata: { ...this.metadata, uploaded_images: nextFiles },
        });
        this.metadata = { ...this.metadata, uploaded_images: nextFiles };
        this.files = nextFiles;
        this.$emit('files-changed', nextFiles);
      } catch (e) {
        console.error('Failed to persist attachments:', e);
        alert('Failed to update attachments. Please try again.');
      } finally {
        this.busy = false;
      }
    },

    addFile({ key, filename }) {
      const entry = {
        key,
        filename,
        uploaded_by: {
          appuser_id: this.currentUser?.id,
          appuser_name: this.currentUser
            ? `${this.currentUser.last_name}, ${this.currentUser.first_name}`
            : 'Unknown User',
          created_at: new Date().toISOString(),
        },
      };
      this.persist([...this.files, entry]);
    },

    removeFile(deletedKey) {
      this.persist(this.files.filter((f) => f.key !== deletedKey));
    },
  },
};
</script>
